import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Tooltip, Typography, Box } from '@mui/material';
import { Mic as MicIcon, MicOff as MicOffIcon, Stop as StopIcon } from '@mui/icons-material';

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
  onError: (error: string) => void;
  accentColor?: string;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onRecordingComplete, onError, accentColor }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Check for microphone permission
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => {
        setHasPermission(true);
      })
      .catch(() => {
        setHasPermission(false);
        onError('Permiso de micrófono denegado');
      });

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [onError]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Detectar el mejor formato de audio soportado por el navegador
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/wav',
        'audio/mp4'
      ];
      
      let selectedMimeType = 'audio/webm'; // default
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        // Usar el mismo tipo MIME que se usó para grabar
        const audioBlob = new Blob(audioChunksRef.current, { type: selectedMimeType });
        onRecordingComplete(audioBlob);
        
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error al iniciar grabación:', error);
      onError('Error al acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (hasPermission === false) {
    return (
      <Tooltip title="Permiso de micrófono requerido">
        <IconButton disabled>
          <MicOffIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        flexShrink: 0,
        minWidth: 'auto',
      }}
    >
      {isRecording && (
        <Typography variant="caption" color="error">
          {formatTime(recordingTime)}
        </Typography>
      )}
      
      <Tooltip title={isRecording ? "Detener grabación" : "Grabar audio"}>
        <IconButton
          onClick={isRecording ? stopRecording : startRecording}
          sx={{
            backgroundColor: isRecording 
              ? 'rgba(244, 67, 54, 0.8)'
              : 'rgba(0, 0, 0, 0.4)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            flexShrink: 0,
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 },
            '&:hover': {
              backgroundColor: isRecording 
                ? 'rgba(244, 67, 54, 1)'
                : 'rgba(0, 0, 0, 0.6)',
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {isRecording ? <StopIcon /> : <MicIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default AudioRecorder;
